import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AlertService} from "../../services";
import {Subject, Subscription} from "rxjs";
import {GalleryService} from "./service/gallery.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {FuseTranslationLoaderService} from "../../../../@fuse/services/translation-loader.service";
import {ComponentTitleService} from "../../services/componentTitle.service";
import {locale as polish} from "../../order/i18n/pl";
import {locale as english} from "../../order/i18n/en";
import {finalize, first} from "rxjs/operators";
import {HttpEventType} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {Gallery} from "./model/gallery";
import {GalleryItem} from "./model/galleryItem";
import {Service} from "../../models/service";
import {DeleteGalleryDialogComponent} from "./dialog/deleteGalleryDialog.component";
import {MatDialog} from "@angular/material/dialog";
import {
    UpdateGalleryItemDialogComponent
} from "./dialog/updateGalleryItemDialog.component";
import {DeleteGalleryItemDialogComponent} from "./dialog/deleteGalleryItemDialog.component";

@Component({
    templateUrl: './galleryDetails.component.html',
    styleUrls: ['./galleryDetails.component.scss',]
})


export class GalleryDetailsComponent implements OnDestroy, OnInit {
    loadingIndicator: boolean;
    @ViewChild("fileDropRef", { static: false }) fileDropEl: ElementRef;
    files: any[] = [];
    uploadSub: Subscription;
    gallery: Gallery;
    items: string[] = [];
    dialogRef: any;


    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        private galleryService: GalleryService,
        private alertService: AlertService,
        private translationLoader: FuseTranslationLoaderService,
        private componentTitleService: ComponentTitleService,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        public _matDialog: MatDialog,
    ) {
        // Set the defaults
        this.loadingIndicator = true;


        // Set the private defaults
        this._unsubscribeAll = new Subject();

        this.translationLoader.loadTranslations(polish, english);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Galleries');
    }

    ngOnInit() {
        this.gallery = this.route.snapshot.data.gallery;

        for (let i=0; i<this.gallery.items.length; i++) {
            this.galleryService.getImageById(this.gallery.id, this.gallery.items[i].id).pipe(first())
                .subscribe((item:string) => {
                    this.gallery.items[i].image = item;
                });
        }
    }

    ngOnDestroy(): void {

    }

    /**
     * on file drop handler
     */
    onFileDropped($event) {
        this.prepareFilesList($event);
    }

    /**
     * handle file from browsing
     */
    fileBrowseHandler(files) {
        this.prepareFilesList(files);
    }

    /**
     * Delete file from files list
     * @param index (File index)
     */
    deleteFile(index: number) {
        if (this.files[index].progress < 100) {
            console.log("Upload in progress.");
            return;
        }
        this.files.splice(index, 1);
    }

    /**
     * Simulate the upload process
     */
    uploadFilesSimulator(index: number) {
        if (index === this.files.length) {
            return;
        } else {
            const formData = new FormData();
            formData.append("image", this.files[index]);
            formData.append("gallery", this.route.snapshot.params['id']);

            const upload$ = this.galleryService.uploadFile(formData).pipe(
                finalize(() => {this.reset(); this.deleteFile(index);})
            );

            this.uploadSub = upload$.subscribe(event => {
                if (event.type == HttpEventType.UploadProgress) {
                    this.files[index].progress = Math.round(100 * (event.loaded / event.total));
                }
            })
        }
    }

    reset() {
        this.uploadSub = null;
        this.items = [];

        this.galleryService.getById(this.gallery.id).pipe(first()).subscribe((gallery: Gallery) => {
            this.gallery = gallery;

            for (let i=0; i<this.gallery.items.length; i++) {
                this.galleryService.getImageById(this.gallery.id, this.gallery.items[i].id).pipe(first())
                    .subscribe((item:string) => {
                        this.items.push(item);
                    });
            }
        });


    }

    cancelUpload() {
        this.uploadSub.unsubscribe();
        this.reset();
    }

    /**
     * Convert Files list to normal array list
     * @param files (Files List)
     */
    prepareFilesList(files: Array<any>) {
        for (const item of files) {
            item.progress = 0;
            this.files.push(item);
        }
        this.fileDropEl.nativeElement.value = "";
        this.uploadFilesSimulator(0);
    }

    /**
     * format bytes
     * @param bytes (File size in bytes)
     * @param decimals (Decimals point)
     */
    formatBytes(bytes, decimals = 2) {
        if (bytes === 0) {
            return "0 Bytes";
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    }

    showDeleteGalleryItemDialog(galleryItem: GalleryItem) {
        this.dialogRef = this._matDialog.open(DeleteGalleryItemDialogComponent, {
            panelClass: 'delete-gallery-item-form-dialog',
            data      : {
                action : 'show',
                galleryItem: galleryItem,
            },
            width: '650px',
        });

        this.dialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                this.galleryService.removeItem(galleryItem.id)
                    .pipe(first())
                    .subscribe(
                        data => {
                            this.alertService.success('Image deleted successfully', { keepAfterRouteChange: true });
                            window.location.reload();
                        },
                        error => {
                            this.alertService.error(error);
                        });

                this.dialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }

    showGalleryItemDialog(galleryItem: GalleryItem) {
        this.dialogRef = this._matDialog.open(UpdateGalleryItemDialogComponent, {
            panelClass: 'update-gallery-item-dialog',
            data      : {
                action : 'show',
                galleryItem: galleryItem,
            },
            width: '650px',
        });

        this.dialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    this.reset();
                    return;
                }

                const data = response.getRawValue();

                this.galleryService.updateItem(data['id'], data)
                    .pipe(first())
                    .subscribe(
                        data => {
                            this.alertService.success('Image updated successfully', { keepAfterRouteChange: true });
                            window.location.reload();
                        },
                        error => {
                            this.alertService.error(error);
                        });

                this.dialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }

}
